import React, { useEffect, useState } from 'react';
import {
  Card, CardBody, CardTitle, Badge, Button, Row, Col, Spinner, Alert,
  Table, Input, FormGroup, Label
} from 'reactstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { setBreadcrumbItems } from '../../store/actions';
import api from '../../utils/api';

const entityLabels = { journalist: 'صحفي', activist: 'ناشط', institution: 'مؤسسة' };
const actionLabels = { create: 'إنشاء', update: 'تعديل', delete: 'حذف' };
const statusColors = { pending: 'warning', approved: 'success', rejected: 'danger', partially_approved: 'info' };

// أسماء الحقول بالعربي
const fieldLabels = {
  full_name: 'الاسم الكامل', title: 'اسم المؤسسة', profile_image: 'صورة البروفايل',
  bio: 'السيرة الذاتية', gender: 'الجنس', id_number: 'رقم الهوية',
  birth_date: 'تاريخ الميلاد', marital_status: 'الحالة الاجتماعية',
  children_names: 'أسماء الأبناء', children_details: 'تفاصيل الأبناء',
  political_orientation: 'التوجه السياسي', country: 'الدولة',
  country_province: 'المحافظة', city_or_area: 'المدينة/القرية',
  address_details: 'تفاصيل العنوان', map_address: 'إحداثيات الموقع',
  specialization: 'التخصص', university_name: 'الجامعة',
  last_work_place: 'آخر مكان عمل', work_sector: 'قطاع العمل',
  linked_institution: 'المؤسسة المرتبطة', linked_activists: 'الناشطون المرتبطون',
  linked_journalists: 'الصحفيون المرتبطون',
  linked_institutions: 'المؤسسات المرتبطة الأخرى',
  whatsapp1: 'واتساب', phone_fax: 'هاتف/فاكس', mobile1: 'جوال',
  oreedo_number: 'رقم أوريدو', email: 'البريد الإلكتروني',
  website: 'الموقع الإلكتروني', social_accounts: 'حسابات التواصل',
  statements: 'الأقوال والتصريحات', videos: 'فيديوهات',
  images: 'صور', attachments: 'مرفقات',
  description: 'الوصف', logo: 'اللوجو', license_status: 'حالة الترخيص',
  reference: 'المرجعية', orientation: 'التوجه',
  responsible_type: 'نوع المسؤول', responsible_journalist_id: 'الصحفي المسؤول',
  responsible_activist_id: 'الناشط المسؤول',
  representative_name: 'اسم الممثل', representative_profile_image: 'صورة الممثل',
  representative_gender: 'جنس الممثل', representative_id_number: 'رقم هوية الممثل',
  representative_birthdate: 'تاريخ ميلاد الممثل',
  representative_political_orientation: 'ميول الممثل السياسية',
  representative_statements: 'تصريحات الممثل',
  youtube_links: 'روابط يوتيوب',
};

const PendingApprovalDetail = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [fieldDecisions, setFieldDecisions] = useState({});

  useEffect(() => {
    props.setBreadcrumbItems('مراجعة الطلب', [
      { title: 'الرئيسية', link: '/dashboard' },
      { title: 'طلبات الموافقة', link: '/dashboard/pending-approvals' },
      { title: 'مراجعة', link: '#' },
    ]);
    fetchItem();
  }, [id]);

  const fetchItem = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/pending-changes/${id}`);
      const data = res.data.data;
      setItem(data);

      // القرارات تبدأ فارغة — الأدمن يختار لكل حقل
      if (data.action === 'update' && data.changed_fields) {
        setFieldDecisions({});
      }
    } catch {
      setError('فشل في جلب تفاصيل الطلب');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (approveAll = true) => {
    // للتعديل: تأكد إن كل الحقول عليها قرار
    if (!approveAll && item?.action === 'update' && item.changed_fields) {
      const undecided = item.changed_fields.filter(f => !fieldDecisions[f]);
      if (undecided.length > 0) {
        alert(`يوجد ${undecided.length} حقل بدون قرار. يرجى اختيار موافقة أو رفض لكل حقل قبل التطبيق.`);
        return;
      }
    }
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await api.post(`/pending-changes/${id}/approve`, {
        approve_all: approveAll,
        field_decisions: approveAll ? {} : fieldDecisions,
        admin_note: adminNote,
      });
      setSuccess('تمت المراجعة بنجاح');
      setTimeout(() => navigate('/dashboard/pending-approvals'), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'فشل في المعالجة');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await api.post(`/pending-changes/${id}/reject`, { admin_note: adminNote });
      setSuccess('تم رفض الطلب');
      setTimeout(() => navigate('/dashboard/pending-approvals'), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'فشل في المعالجة');
    } finally {
      setSubmitting(false);
    }
  };

  const setFieldDecision = (field, decision) => {
    setFieldDecisions(prev => ({ ...prev, [field]: decision }));
  };

  const clearFieldDecision = (field) => {
    setFieldDecisions(prev => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  };

  const approveAllFields = () => {
    const updated = {};
    (item.changed_fields || []).forEach(f => { updated[f] = 'approved'; });
    setFieldDecisions(updated);
  };

  const rejectAllFields = () => {
    const updated = {};
    (item.changed_fields || []).forEach(f => { updated[f] = 'rejected'; });
    setFieldDecisions(updated);
  };

  const formatValue = (val) => {
    if (val === null || val === undefined || val === '') return <span className="text-muted">— فارغ —</span>;
    if (typeof val === 'object') return <pre className="mb-0" style={{ fontSize: '0.8rem', maxHeight: 120, overflow: 'auto' }}>{JSON.stringify(val, null, 2)}</pre>;
    return String(val);
  };

  if (loading) {
    return <div className="text-center py-5"><Spinner color="primary" /><p className="mt-2">جار التحميل...</p></div>;
  }

  if (!item) {
    return <Alert color="danger">لم يتم العثور على الطلب</Alert>;
  }

  const isPending = item.status === 'pending';

  return (
    <div>
      {error && <Alert color="danger">{error}</Alert>}
      {success && <Alert color="success">{success}</Alert>}

      {/* معلومات الطلب */}
      <Card className="mb-3">
        <CardBody>
          <Row>
            <Col md={8}>
              <div className="d-flex align-items-center mb-3">
                <Badge color={item.action === 'create' ? 'success' : item.action === 'update' ? 'info' : 'danger'} className="me-2 font-size-14 p-2">
                  {actionLabels[item.action]}
                </Badge>
                <Badge color="primary" className="me-2 font-size-14 p-2">
                  {entityLabels[item.entity_type]}
                </Badge>
                <Badge color={statusColors[item.status]} className="font-size-14 p-2">
                  {item.status === 'pending' ? 'معلّق' : item.status === 'approved' ? 'موافق عليه' : item.status === 'rejected' ? 'مرفوض' : 'موافقة جزئية'}
                </Badge>
              </div>
              <h4 className="mb-2">
                {item.new_data?.full_name || item.new_data?.title || item.original_data?.full_name || item.original_data?.title || '—'}
              </h4>
            </Col>
            <Col md={4} className="text-end">
              <div className="text-muted mb-1">
                <i className="mdi mdi-account me-1"></i>
                <strong>مقدم الطلب:</strong> {item.user?.name}
              </div>
              <div className="text-muted mb-1">
                <i className="mdi mdi-email me-1"></i>
                {item.user?.email}
              </div>
              <div className="text-muted">
                <i className="mdi mdi-clock-outline me-1"></i>
                {(() => { const d = new Date(item.created_at); return `${d.getDate()}/${d.getMonth()+1}/${d.getFullYear()} ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`; })()}
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* === إنشاء جديد === */}
      {item.action === 'create' && (
        <Card className="mb-3">
          <CardBody>
            <CardTitle className="h5 mb-3">
              <i className="mdi mdi-plus-circle text-success me-2"></i>
              البيانات المطلوب إنشاؤها
            </CardTitle>
            <Table bordered responsive className="mb-0">
              <thead className="table-light">
                <tr><th style={{width: '30%'}}>الحقل</th><th>القيمة</th></tr>
              </thead>
              <tbody>
                {Object.entries(item.new_data || {}).filter(([k, v]) => v !== null && v !== '' && k !== 'id' && k !== 'created_at' && k !== 'updated_at').map(([key, val]) => (
                  <tr key={key}>
                    <td className="fw-bold">{fieldLabels[key] || key}</td>
                    <td>{formatValue(val)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      )}

      {/* === تعديل === */}
      {item.action === 'update' && (
        <Card className="mb-3">
          <CardBody>
            <CardTitle className="h5 mb-3">
              <i className="mdi mdi-pencil text-info me-2"></i>
              مقارنة التعديلات
              {isPending && (
                <span className="float-end">
                  <Button color="success" size="sm" className="me-2" onClick={approveAllFields}>
                    <i className="mdi mdi-check-all me-1"></i>موافقة الكل
                  </Button>
                  <Button color="danger" size="sm" onClick={rejectAllFields}>
                    <i className="mdi mdi-close-circle me-1"></i>رفض الكل
                  </Button>
                </span>
              )}
            </CardTitle>
            <Table bordered responsive className="mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{width: '20%'}}>الحقل</th>
                  <th style={{width: '30%'}}>القيمة الأصلية</th>
                  <th style={{width: '30%'}}>القيمة الجديدة</th>
                  {isPending && <th style={{width: '20%'}} className="text-center">القرار</th>}
                </tr>
              </thead>
              <tbody>
                {(item.changed_fields || []).map(field => {
                  const oldVal = item.original_data?.[field];
                  const newVal = item.new_data?.[field];
                  const decision = fieldDecisions[field]; // undefined = لم يُحدد بعد
                  const savedDecision = item.field_decisions?.[field];
                  return (
                    <tr key={field} className={!isPending ? (savedDecision === 'approved' ? 'table-success' : savedDecision === 'rejected' ? 'table-danger' : '') : (decision === 'approved' ? 'table-success' : decision === 'rejected' ? 'table-danger' : '')}>
                      <td className="fw-bold">{fieldLabels[field] || field}</td>
                      <td className="bg-light">{formatValue(oldVal)}</td>
                      <td>{formatValue(newVal)}</td>
                      {isPending && (
                        <td className="text-center">
                          {!decision ? (
                            // لم يُتخذ قرار بعد — زرين
                            <div className="d-flex justify-content-center gap-1">
                              <Button color="success" size="sm" onClick={() => setFieldDecision(field, 'approved')}>
                                <i className="mdi mdi-check me-1"></i>موافقة
                              </Button>
                              <Button color="danger" size="sm" onClick={() => setFieldDecision(field, 'rejected')}>
                                <i className="mdi mdi-close me-1"></i>رفض
                              </Button>
                            </div>
                          ) : (
                            // تم الاختيار — عرض النتيجة مع زر تراجع
                            <div className="d-flex align-items-center justify-content-center gap-2">
                              <Badge color={decision === 'approved' ? 'success' : 'danger'} className="font-size-13 p-2">
                                {decision === 'approved' ? <><i className="mdi mdi-check-circle me-1"></i>موافق عليه</> : <><i className="mdi mdi-close-circle me-1"></i>مرفوض</>}
                              </Badge>
                              <Button color="light" size="sm" className="border" onClick={() => clearFieldDecision(field)} title="تراجع">
                                <i className="mdi mdi-undo"></i>
                              </Button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      )}

      {/* === حذف === */}
      {item.action === 'delete' && (
        <Card className="mb-3 border-danger">
          <CardBody>
            <CardTitle className="h5 mb-3 text-danger">
              <i className="mdi mdi-delete text-danger me-2"></i>
              البيانات المطلوب حذفها
            </CardTitle>
            <Table bordered responsive className="mb-0">
              <thead className="table-light">
                <tr><th style={{width: '30%'}}>الحقل</th><th>القيمة</th></tr>
              </thead>
              <tbody>
                {Object.entries(item.original_data || {}).filter(([k, v]) => v !== null && v !== '' && k !== 'id' && k !== 'created_at' && k !== 'updated_at').map(([key, val]) => (
                  <tr key={key}>
                    <td className="fw-bold">{fieldLabels[key] || key}</td>
                    <td>{formatValue(val)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      )}

      {/* أزرار القرار */}
      {isPending && (
        <Card>
          <CardBody>
            <FormGroup>
              <Label className="fw-bold">ملاحظات (اختياري)</Label>
              <Input
                type="textarea"
                rows={3}
                value={adminNote}
                onChange={e => setAdminNote(e.target.value)}
                placeholder="أضف ملاحظة للمستخدم..."
              />
            </FormGroup>

            {/* رسالة تحذير فوق الأزرار مباشرة */}
            {item.action === 'update' && item.changed_fields && item.changed_fields.some(f => !fieldDecisions[f]) && (
              <Alert color="warning" className="mb-3">
                <i className="mdi mdi-alert-circle me-1"></i>
                يوجد {item.changed_fields.filter(f => !fieldDecisions[f]).length} حقل بدون قرار. يرجى اختيار موافقة أو رفض لكل حقل.
              </Alert>
            )}

            <div className="d-flex gap-2 mt-3">
              {item.action === 'update' ? (
                <>
                  <Button color="primary" onClick={() => handleApprove(false)} disabled={submitting} className="flex-fill">
                    {submitting ? <Spinner size="sm" /> : <><i className="mdi mdi-check me-1"></i>تطبيق القرارات</>}
                  </Button>
                </>
              ) : (
                <>
                  <Button color="success" onClick={() => handleApprove(true)} disabled={submitting} className="flex-fill">
                    {submitting ? <Spinner size="sm" /> : <><i className="mdi mdi-check me-1"></i>موافقة</>}
                  </Button>
                  <Button color="danger" onClick={handleReject} disabled={submitting} className="flex-fill">
                    {submitting ? <Spinner size="sm" /> : <><i className="mdi mdi-close me-1"></i>رفض</>}
                  </Button>
                </>
              )}
              <Button color="secondary" onClick={() => navigate('/dashboard/pending-approvals')} className="flex-fill">
                رجوع
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* إذا تمت المراجعة مسبقاً */}
      {!isPending && item.admin_note && (
        <Card className="mt-3">
          <CardBody>
            <h6 className="text-muted">ملاحظات المراجع:</h6>
            <p>{item.admin_note}</p>
            {item.reviewer && <small className="text-muted">المراجع: {item.reviewer.name} — {(() => { const d = new Date(item.reviewed_at); return `${d.getDate()}/${d.getMonth()+1}/${d.getFullYear()}`; })()}</small>}
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default connect(null, { setBreadcrumbItems })(PendingApprovalDetail);
